import { db } from '../../data/base';
import { collection, doc, getDoc, setDoc, updateDoc, getDocs } from 'firebase/firestore';
import {
    getCatalogue,
    saveCatalogue,
    updateCatalogue
} from './catalogueBaseService';

/**
 * Ottiene un catalogo pubblicato dal database
 * @param {string} catalogueId - L'ID del catalogo
 * @returns {Promise<object|null>} - I dati del catalogo o null se non esiste
 */
export async function getPublishedCatalogue(catalogueId) {
    return getCatalogue('Catalogues', catalogueId);
}

/**
 * Salva un nuovo catalogo pubblicato
 * @param {object} formData - I dati del form
 * @returns {Promise<string>} - L'ID del catalogo salvato
 */
export async function savePublishedCatalogue(formData) {
    return saveCatalogue('Catalogues', formData);
}

/**
 * Aggiorna un catalogo pubblicato esistente
 * @param {string} catalogueId - L'ID del catalogo
 * @param {object} formData - I dati del form
 * @returns {Promise<string>} - L'ID del catalogo aggiornato
 */
export async function updatePublishedCatalogue(catalogueId, formData) {
    return updateCatalogue('Catalogues', catalogueId, formData);
}

/**
 * Verifica se un catalogo pubblicato ha una bozza associata
 * @param {string} catalogueId - L'ID del catalogo pubblicato
 * @returns {Promise<string|null>} - L'ID della bozza o null se non esiste
 */
export async function checkForExistingDraft(catalogueId) {
    try {
        const catalogueRef = doc(db, 'Catalogues', catalogueId);
        const catalogueSnap = await getDoc(catalogueRef);

        if (!catalogueSnap.exists()) {
            return null;
        }

        const catalogueData = catalogueSnap.data();
        return catalogueData.draftedCatalogRef || null;
    } catch (error) {
        console.error('Error checking for existing draft:', error);
        return null;
    }
}

/**
 * Aggiorna il riferimento alla bozza nel catalogo pubblicato
 * @param {string} catalogueId - L'ID del catalogo pubblicato
 * @param {string} draftId - L'ID della bozza
 * @returns {Promise<void>}
 */
export async function updateDraftReference(catalogueId, draftId) {
    try {
        const catalogueRef = doc(db, 'Catalogues', catalogueId);
        await setDoc(catalogueRef, {
            draftedCatalogRef: draftId
        }, { merge: true });

    } catch (error) {
        console.error('Error updating draft reference:', error);
        throw error;
    }
}

/**
 * Rimuove il riferimento alla bozza nel catalogo pubblicato
 * @param {string} catalogueId - L'ID del catalogo pubblicato
 * @returns {Promise<void>}
 */
export async function removeDraftReference(catalogueId) {
    try {
        const catalogueRef = doc(db, 'Catalogues', catalogueId);
        await updateDoc(catalogueRef, {
            draftedCatalogRef: null
        });

    } catch (error) {
        console.error('Error removing draft reference:', error);
        throw error;
    }
}

/**
 * Ottiene tutti i cataloghi pubblicati
 * @returns {Promise<Array>} - Array di cataloghi
 */
export async function getAllPublishedCatalogues() {
    try {
        const cataloguesRef = collection(db, 'Catalogues');
        const querySnapshot = await getDocs(cataloguesRef);

        const catalogues = [];
        querySnapshot.forEach((doc) => {
            catalogues.push({
                id: doc.id,
                ...doc.data()
            });
        });

        return catalogues;
    } catch (error) {
        console.error('Error getting all published catalogues:', error);
        throw error;
    }
}
